import React, {useState, useRef, useEffect} from 'react';
import './Identification.css';

import MorphologicalInterface from './interfaces/MorphologicalInterface';
import LexicalInterface from './interfaces/LexicalInterface';

/**
 * Per-word morphological fields for completed sandhi sentences (edit mode).
 * Layout: one wrapping row of boxes; each box — centered title, then 3 label+input rows.
 * @param {Array<{ inflection: string, lemma: string, lexical_category: string }>} rows
 * @param {string[]} words — same length as rows; token text shown as each box title
 * @param {(wordIndex: number, field: 'inflection'|'lemma'|'lexical_category', value: string) => void} onFieldChange
 * @param {string} [xmlid] — parent sentence xmlid (used by the Save button)
 * @param {(payload: { xmlid: string, rows: Array, words: string[] }) => (void|Promise<void>)} [onSave]
 *        — invoked when the Save button is clicked; receives all rows for this sentence
 */
function Identification({ rows = [], words = [], onFieldChange, xmlid, onSave }) {
  const fields = [
    { key: 'inflection', label: 'Inflection' },
    { key: 'lemma', label: 'Lemma' },
    { key: 'lexical_category', label: 'Lexical category' },
  ];

  const [interfaceTitle, setInterfaceTitle] = useState('');
  const [interfaceIndex, setInterfaceIndex] = useState(0);
  const [showInterface, setShowInterface] = useState(false);
  const [showLexInterface, setShowLexInterface] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const [ansState, setAnsState] = useState(rows);

  const updateField = (wi, field, value) => {
    setAnsState((prev) => {
      const next = [...prev];
      if (!next[wi]) {
        next[wi] = { inflection: '', lemma: '', lexical_category: '' };
      }
      next[wi] = { ...next[wi], [field]: value };
      return next;
    });
    onFieldChange(wi, field, value);
  };

  const handleSetAns = (newValues) => {
    const updated = newValues?.[interfaceIndex];
    const previous = ansState?.[interfaceIndex];
    if (!updated || !previous) return;

    const trackedFields = ['inflection', 'lemma', 'lexical_category'];
    const changed = trackedFields.filter(f => updated[f] !== previous[f]);
    if (changed.length === 0) return;

    setAnsState(prev => {
      const next = [...prev];
      const merged = { ...next[interfaceIndex] };
      changed.forEach(f => { merged[f] = updated[f]; });
      next[interfaceIndex] = merged;
      return next;
    });
    changed.forEach(f => onFieldChange(interfaceIndex, f, updated[f]));
  };
  
  //Enter = Submit
	const [btn, setBtn]=useState(null)

	const interfaceOpen =useRef(null);
	useEffect(()=>{
    interfaceOpen.current = showInterface || showLexInterface;
	}, [showInterface, showLexInterface]);
	
	const handleKeyDown=(event)=>{
		// console.log('key=', event.key)
		if(event.key==='Enter' && !interfaceOpen.current){
			event.preventDefault();
			btn.click();
		}
	};
	useEffect(() => {
    if (!btn) return;
    const handler = (e) => handleKeyDown(e);
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [btn]);

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <MorphologicalInterface
        show={showInterface}
        onHide={()=>setShowInterface(false)}
        title={interfaceTitle}
        index={interfaceIndex}
        ansstate={ansState}
        setans={handleSetAns}
			/>

      <LexicalInterface
        show={showLexInterface}
        onHide={()=>setShowLexInterface(false)}
        title={interfaceTitle}
        index={interfaceIndex}
        ansstate={ansState}
        setans={handleSetAns}
      />

      <div className="identification-row">
        {ansState.map((row, wi) => (
          <div key={wi} className="identification-box">
            <div className="identification-box-title" title={words[wi] ?? ''}>
              {words[wi] ?? '\u00a0'}
            </div>
            <div className="identification-box-fields">
              {fields.map(({ key, label }) => (
                <div key={key} className="identification-field-row">
                  <label className="identification-label" htmlFor={`id-${wi}-${key}`}>
                    {label}
                  </label>
                  <input
                    id={`id-${wi}-${key}`}
                    type="text"
                    className="identification-input"
                    value={row?.[key] ?? ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (key === 'inflection' && (value === '?' || value === '/')) {
                        setInterfaceTitle(words[wi] ?? '');
                        setInterfaceIndex(wi);
                        setShowInterface(true);
                        return;
                      }
                      if (key === 'lexical_category' && (value === '?' || value === '/')) {
                        setInterfaceTitle(words[wi] ?? '');
                        setInterfaceIndex(wi);
                        setShowLexInterface(true);
                        return;
                      }
                      updateField(wi, key, value);
                    }}
                  />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="identification-actions">
        {saveError && (
          <span className="identification-save-error">{saveError}</span>
        )}
        <button
          type="button"
          className="save-button"
          disabled={saving || !onSave}
          onClick={async (e) => {
            e.stopPropagation();
            if (!onSave) return;
            setSaving(true);
            setSaveError(null);
            try {
              await onSave({ xmlid, rows: ansState, words });
            } catch (err) {
              console.error('Error saving morphology:', err);
              setSaveError(err?.message || 'Failed to save');
            } finally {
              setSaving(false);
            }
          }}
          title="Save morphology for all words in this sentence"
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
}

export default Identification;
